/*
 * km2-product-model.js — the ONE card-product contract, and the two adapters onto it.
 *
 * Plan 20260901-050844 (T2), phase 0 of the km2-card program.
 *
 * WHY THIS EXISTS. Six divergent mappers used to build the card contract, and none of them
 * carried a variant `id` or `title` — so a size picker had nothing to render. This module
 * collapses those six into two adapters over one factory:
 *
 *   productFromStorefrontNode(node)          — Storefront GraphQL nodes (edges/nodes, GID ids,
 *                                              decimal-string money)
 *   productFromRestProduct(product, {scale}) — REST / Ajax payloads (flat arrays, numeric ids,
 *                                              money in CENTS or decimal strings — hence `scale`)
 *
 * DATA ONLY. This module never touches the DOM and never decides availability POLICY. It carries
 * a product-level availability field; `assets/kmf-filters.js` keeps ownership of the Storefront
 * `availHandles` overlay and of applyFilter/applySort. If a later phase moves that, it moves as
 * an INJECTED resolver, never as a model-computed value.
 *
 * Contract:
 *   { title, handle, vendor, product_type, tags:[], url, images:[{src}],
 *     availableForSale: bool,
 *     variants:[{ id, title, price, compare_at_price, available, synthetic? }] }
 *
 * `product_type` is the type key, not `productType` — five of the six emitters already emitted it
 * and the shared builder reads only `p.product_type` for its type badge (round-4 r4-f5).
 *
 * Derived read (finding 513, owner 2026-09-11):
 *   displayPrice(product, range?) — the ONE rule for "which price does a card show". Every card
 *   renderer and the grid's price ordering read this, so the number a shopper sorts on is the
 *   number printed on the card. See the function for the rule.
 */
(function () {
  if (window.KM2ProductModel) return; // idempotent — a second <script> must not replace instance one

  /* ---------------------------------------------------------------- primitives */

  /**
   * Extract the numeric variant id from a Storefront GID, a number, or a numeric string.
   * Returns null for anything that is not a variant id — never NaN, never a partial parse.
   */
  function variantIdFromGid(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    var s = String(v);
    var m = s.match(/(\d+)\s*$/); // trailing digits: the GID's last segment, or a bare id
    if (!m) return null;
    // Reject strings that are neither a pure number nor a gid:// form — "not-an-id" has no digits
    // and already returned; "abc123" would otherwise slip through.
    if (!/^\d+$/.test(s) && s.indexOf('/') === -1) return null;
    var n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Coerce a money value to a number in CURRENCY UNITS, given the source's scale.
   *
   * scale 100 — the value is in cents (Storefront REST/Ajax): 1150 -> 11.5
   * scale 1   — the value is already in units (products.json decimal strings): "11.50" -> 11.5
   *
   * The scale is a PARAMETER, not a constant, because the sources genuinely disagree: the three
   * Ajax mappers divide by 100 while products.json ships decimal strings. A fixed 1/100 would be
   * wrong by a factor of 100 on that path.
   *
   * Returns null (never NaN, never 0) when the value is absent or non-numeric, so callers can
   * distinguish "no compare-at price" from "free".
   */
  function toMoney(v, scale) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'object') return null; // {} and [] are not money
    var n = Number(v);
    if (!Number.isFinite(n)) return null;
    var d = Number(scale);
    if (!Number.isFinite(d) || d === 0) d = 1;
    return n / d;
  }

  /**
   * Normalise an image list to [{src}], dropping duplicates and empties while preserving
   * first-seen order. Accepts bare url strings as well as {src} / {url} objects, because the
   * six sources disagreed about that too.
   */
  function dedupeImages(list) {
    if (!list || !list.length) return [];
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item) continue;
      var src = typeof item === 'string' ? item : item.src || item.url || '';
      if (!src || seen[src]) continue;
      seen[src] = true;
      out.push({ src: src });
    }
    return out;
  }

  /* ---------------------------------------------------------------- the factory */

  /** Loud in a dev context, quiet-but-visible in production. */
  function isDebugContext() {
    try {
      if (window.Shopify && window.Shopify.designMode) return true;
      return String(window.location && window.location.search).indexOf('km2debug') !== -1;
    } catch (e) {
      return false;
    }
  }

  /**
   * Build a card product from an ALREADY-NORMALISED shape. Adapters call this; call sites that
   * hand-build a product object should call it too, so there is exactly one contract.
   *
   * Variant title guard: a variant with no `title` cannot render a size picker, which is the
   * whole point of this plan — so it is reported. It THROWS in a dev context and otherwise
   * console.warns, naming ONLY the handle and the offending field. The product object is never
   * logged: it can be large and can carry customer-visible copy.
   *
   * A `synthetic: true` variant is EXEMPT (round-2 r2-f5). `toCardModel` synthesises a fallback
   * variant when the edges list is empty — it carries price and availability, has no title and no
   * id by design, and quick-add must still see it so a product-level-available product keeps its
   * button.
   */
  function buildCardProduct(input) {
    var src = input || {};
    var handle = src.handle || '';
    var variants = [];
    var raw = src.variants || [];

    for (var i = 0; i < raw.length; i++) {
      var v = raw[i] || {};
      var variant = {
        id: v.id === undefined ? null : v.id,
        title: v.title === undefined ? null : v.title,
        price: v.price === undefined ? null : v.price,
        compare_at_price: v.compare_at_price === undefined ? null : v.compare_at_price,
        available: !!v.available,
      };
      if (v.synthetic) variant.synthetic = true;

      if (!variant.synthetic && !variant.title) {
        var msg =
          '[km2-product-model] variant missing required field "title" on product handle "' +
          handle +
          '"';
        if (isDebugContext()) throw new Error(msg);
        console.warn(msg);
      }
      variants.push(variant);
    }

    return {
      title: src.title || '',
      handle: handle,
      // `vendor` is the brand headline every card renders (km2-product-card.js build(), and
      // kmf-filters.js cardHtml) — dropping it blanks the brand line on every surface.
      // `tags` carries the type-* tags the fragrance guards read (ebcIsFragrance, isFragrance).
      // Both are part of the documented contract; both were missing until the T5 migration
      // surfaced it, which is exactly what migrating the real emitters is for.
      vendor: src.vendor || '',
      product_type: src.product_type || '',
      tags: Array.isArray(src.tags) ? src.tags : [],
      url: src.url || (handle ? '/products/' + handle : ''),
      images: dedupeImages(src.images),
      availableForSale: !!src.availableForSale,
      variants: variants,
    };
  }

  /* ---------------------------------------------------------------- derived reads */

  /**
   * The price a card shows for a product — finding 513 (owner rule, 2026-09-11).
   *
   * WHY. Shopify's collection `PRICE` sort ranks a product on its cheapest variant EVEN WHEN THAT
   * VARIANT IS SOLD OUT, while the cards printed `variants[0]` — Shopify's position-1 size, which is
   * usually the flagship bottle. Cartier La Panthère ranked #1 under "Price: Low to High" on a
   * sold-out $1 mini and printed its $85 tester. Measured 2026-09-11: 7 of 20 multi-size products
   * in /collections/women print a number that is not the one they were ranked on.
   *
   * THE RULE. Pick the cheapest IN-STOCK size. If a price range is supplied (the shopper's price
   * facet), restrict to in-stock sizes inside it first — the card must not read "From $2" under a
   * "$25+" pill. Fall back progressively: in-stock in range → in-stock → any size in range → any
   * size. `from` is true when a dearer size exists in the SAME candidate set, so "From $2.00" is
   * only claimed when there is genuinely something dearer to buy. Ties go to the lowest index, so
   * the chosen variant is deterministic (quick-add reads its id). Compare-at is read from the
   * CHOSEN variant, never from variants[0] — otherwise a $2 sample inherits a $100 strike-through.
   *
   * On the typed /search path products.json's per-variant `available` is wrong for this store
   * (bug_log#17: false for the whole catalogue), so "in stock" finds nothing and the rule degrades
   * to "cheapest size overall" — which is the number that path's client sort ranks on. Consistent.
   *
   * Returns { price, compare_at_price, from, variant }; price/variant are null when no size has a
   * numeric price. Pure — reads the contract's `available` field, decides no availability POLICY.
   */
  function displayPrice(product, range) {
    var variants = (product && product.variants) || [];
    var min = range && typeof range.min === 'number' && Number.isFinite(range.min) ? range.min : null;
    var max = range && typeof range.max === 'number' && Number.isFinite(range.max) ? range.max : null;

    var priced = [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var n = v ? parseFloat(v.price) : NaN;
      if (Number.isFinite(n)) priced.push({ v: v, n: n });
    }
    if (!priced.length) return { price: null, compare_at_price: null, from: false, variant: null };

    function inRange(e) { return (min === null || e.n >= min) && (max === null || e.n <= max); }
    function inStock(e) { return !!e.v.available; }
    function both(e) { return inStock(e) && inRange(e); }

    // Progressive fallback — the first non-empty candidate set wins.
    var sets = [priced.filter(both), priced.filter(inStock), priced.filter(inRange), priced];
    var set = null;
    for (var s = 0; s < sets.length && !set; s++) if (sets[s].length) set = sets[s];

    var pick = set[0];
    var dearest = set[0].n;
    for (var k = 1; k < set.length; k++) {
      if (set[k].n < pick.n) pick = set[k];        // strict: ties keep the lowest index
      if (set[k].n > dearest) dearest = set[k].n;
    }
    var cmp = parseFloat(pick.v.compare_at_price);
    return {
      price: pick.n,
      compare_at_price: Number.isFinite(cmp) ? cmp : null,
      from: dearest > pick.n,
      variant: pick.v,
    };
  }

  /* ---------------------------------------------------------------- adapters */

  /** The fallback `toCardModel` has always synthesised when a product has no fetched variants. */
  function syntheticVariant(price, compareAt, available) {
    return {
      id: null,
      title: null,
      price: price === undefined ? null : price,
      compare_at_price: compareAt === undefined ? null : compareAt,
      available: !!available,
      synthetic: true,
    };
  }

  /**
   * Adapter A — Storefront GraphQL node.
   *
   * Money arrives as a decimal STRING in currency units (`price.amount`), so the scale is 1.
   * Availability comes from the node's own `availableForSale` and falls back to the first
   * variant's flag when the query did not select it.
   */
  function productFromStorefrontNode(node) {
    var n = node || {};

    // featuredImage FIRST, then the images connection. Order matters: it is the card's first
    // frame and the rails' hover-cycle start, so getting it wrong changes every card's
    // appearance. dedupeImages then drops the featured image's repeat inside the connection.
    var images = [];
    if (n.featuredImage && (n.featuredImage.url || n.featuredImage.src)) {
      images.push(n.featuredImage.url || n.featuredImage.src);
    }
    var imgEdges = (n.images && n.images.edges) || [];
    for (var i = 0; i < imgEdges.length; i++) {
      var img = imgEdges[i] && imgEdges[i].node;
      if (img) images.push(img.url || img.src || '');
    }

    var varEdges = (n.variants && n.variants.edges) || [];
    var variants = [];
    for (var j = 0; j < varEdges.length; j++) {
      var vn = varEdges[j] && varEdges[j].node;
      if (!vn) continue;
      variants.push({
        id: variantIdFromGid(vn.id),
        title: vn.title,
        price: toMoney(vn.price && vn.price.amount, 1),
        compare_at_price: toMoney(vn.compareAtPrice && vn.compareAtPrice.amount, 1),
        available: !!vn.availableForSale,
      });
    }

    var firstAvailable = variants.length ? variants[0].available : false;
    if (!variants.length) variants.push(syntheticVariant(null, null, firstAvailable));

    // Product-level availability is AUTHORITATIVE for the sold-out badge: a product can pass a
    // collection's available:true filter via a later variant while the first FETCHED variant is
    // sold out (round-1 f4). Only fall back when the field was not selected at all.
    var availableForSale =
      n.availableForSale === undefined || n.availableForSale === null
        ? firstAvailable
        : !!n.availableForSale;

    return buildCardProduct({
      title: n.title,
      handle: n.handle,
      vendor: n.vendor,
      product_type: n.productType, // normalised onto product_type
      tags: n.tags,
      url: n.url || (n.handle ? '/products/' + n.handle : ''),
      images: images,
      availableForSale: availableForSale,
      variants: variants,
    });
  }

  /**
   * Adapter B — REST / Ajax product payload.
   *
   * `opts.scale` is REQUIRED in spirit and defaults to 100 (cents), which is what the three Ajax
   * mappers use. The typed-/search products.json path must pass `{ scale: 1 }`: its prices are
   * decimal strings already in currency units, and dividing them by 100 would be wrong by 100x.
   *
   * REST payloads carry NO product-level availability field, so it is DERIVED from the variants
   * supplied — the honest answer for this shape, and the reason the field is set here rather than
   * assumed by the factory.
   */
  function productFromRestProduct(product, opts) {
    var p = product || {};
    var scale = opts && opts.scale !== undefined ? opts.scale : 100;

    var raw = p.variants || [];
    var variants = [];
    var anyAvailable = false;
    for (var i = 0; i < raw.length; i++) {
      var v = raw[i] || {};
      if (v.available) anyAvailable = true;
      variants.push({
        id: variantIdFromGid(v.id),
        title: v.title,
        price: toMoney(v.price, scale),
        compare_at_price: toMoney(v.compare_at_price, scale),
        available: !!v.available,
      });
    }
    if (!variants.length) variants.push(syntheticVariant(null, null, false));

    return buildCardProduct({
      title: p.title,
      handle: p.handle,
      vendor: p.vendor,
      product_type: p.product_type || p.type, // REST calls it `type`
      tags: p.tags,
      url: p.url || (p.handle ? '/products/' + p.handle : ''),
      images: p.images,
      availableForSale: anyAvailable,
      variants: variants,
    });
  }

  // A Shopify-CDN rendition `width` px wide: `_<w>x` before the extension, query kept. The ONE resize
  // helper — kmf-filters.js, km2-brand-tiles.js and km2-product-card.js delegate here (D14).
  // A url that already carries a size (`_160x`, `_300x200` — e.g. Admin GraphQL transform urls) has it
  // REPLACED: a stacked `_160x_160x` does not exist on the CDN (404).
  function imageUrl(url, width) {
    if (!url) return '';
    return String(url).replace(/(?:_\d+x\d*)?\.(jpe?g|png|webp|gif)(\?.*)?$/i, '_' + width + 'x.$1$2');
  }

  window.KM2ProductModel = {
    imageUrl: imageUrl,
    buildCardProduct: buildCardProduct,
    dedupeImages: dedupeImages,
    variantIdFromGid: variantIdFromGid,
    toMoney: toMoney,
    productFromStorefrontNode: productFromStorefrontNode,
    productFromRestProduct: productFromRestProduct,
    displayPrice: displayPrice,
  };
})();
